/**
 * orderTotals.ts — Aritmética del dinero del ticket, COMPARTIDA por los motores de impresión
 * (IR webble, ticket Electron y PDF pdfMake).
 *
 * Antes cada motor traía su propia copia y NO coincidían: `computeOrderTotal` vivía solo en
 * `webble/ticketModel.ts`, el ticket Electron repetía la fórmula a mano en `getOrderFooter.ts`
 * y el PDF imprimía `order.total` del servidor. El "Cambio" estaba escrito TRES veces contra
 * TRES bases distintas — Electron llegaba a imprimir un total y a restar otro número en el
 * mismo papel. Aquí vive la única copia.
 *
 * FUENTE DEL "Total a pagar": `pedido.total` del servidor (`printedOrderTotal`), en las tres
 * rutas. Es el importe que el cliente vio y que Stripe/Clip cobraron de verdad; no existe
 * ningún cargo oculto que le falte. `computeOrderTotal` (subtotal − descuento + propina +
 * envío) sigue existiendo, pero solo como la suma de los COMPONENTES del desglose, para
 * medir si el desglose cuadra con lo cobrado.
 *
 * Cuando NO cuadra —137 de 302,617 pedidos en 90 días, donde la propina se cobró pero se
 * guardó en 0— la diferencia se imprime como una línea residual (`RESIDUAL_LABEL`) en vez de
 * taparse: la información no existe en los campos, así que el papel dice cuánto falta en vez
 * de mentir sumando mal.
 *
 * Coerción por término (TKCN-543/542): el BE puede mandar estos campos como string y el `+`
 * CONCATENARÍA en vez de sumar. `roundToCents` cierra la deriva binaria antes de que el monto
 * quede guardado en una variable (PRJ-010) — `formatToCurrency` limpia la vista, no el valor.
 */

import { roundToCents, toFiniteAmount } from '@utils/formats';

// Campos de dinero que la aritmética del ticket consume. Se acepta la forma laxa porque el
// pedido puede llegar del BE con estos valores como string o ausentes.
type OrderAmounts = {
  subtotal?: unknown;
  coupon_amount?: unknown;
  propina?: unknown;
  costo_envio?: unknown;
  total?: unknown;
};

// Etiqueta de la línea residual. NEUTRA a propósito: en el 100% de los casos observados el
// residuo es propina cobrada y guardada en 0, pero eso no se puede garantizar a futuro, así
// que llamarla "Propina" sería afirmar algo que el dato no dice. "Ajuste" además se lee bien
// con residuo positivo y negativo.
export const RESIDUAL_LABEL = 'Ajuste';

// Por debajo de un centavo la diferencia es ruido de punto flotante, no dinero.
const RESIDUAL_EPSILON = 0.01;

/**
 * Suma de los componentes del desglose: subtotal − descuento + propina + envío.
 *
 * @param {OrderAmounts} pedido Pedido (o pedido sintético de mesa) con sus montos crudos.
 * @returns {number} La suma de los componentes, redondeada a centavos.
 */
export function computeOrderTotal(pedido: OrderAmounts): number {
  return roundToCents(
    toFiniteAmount(pedido.subtotal) -
      toFiniteAmount(pedido.coupon_amount) +
      toFiniteAmount(pedido.propina) +
      toFiniteAmount(pedido.costo_envio),
  );
}

/**
 * Cambio a devolver: lo que el cliente entrega menos el total que ESE ticket imprime.
 *
 * La base (`total`) la pasa el motor que llama, para que el papel nunca reste contra un
 * número distinto del que imprimió. Un resultado negativo no es un cambio sino un faltante
 * (el cajero capturó menos de lo que cuesta el pedido): se devuelve 0 en vez de imprimir un
 * "Cambio: -$50.00", que era lo que salía antes.
 *
 * @param {unknown} paidWith Con cuánto paga el cliente (`cliente_paga_con`, string del BE).
 * @param {number} total Total que ESTE ticket imprime como "Total a pagar".
 * @returns {number} El cambio, redondeado a centavos y nunca negativo.
 */
export function computeChange(paidWith: unknown, total: number): number {
  const change = roundToCents(toFiniteAmount(paidWith) - toFiniteAmount(total));
  return change > 0 ? change : 0;
}

/**
 * Total que el ticket IMPRIME como "Total a pagar": `pedido.total` del servidor.
 *
 * Sin fallback a `computeOrderTotal` a propósito. Si `total` llega 0 o imparseable se imprime
 * $0.00 y el residuo dispara el aviso — esa es la red. Un `||` reintroduciría justo la
 * ambigüedad de dos totales distintos que este módulo existe para matar (y un pedido gratis
 * legítimo debe imprimir $0.00).
 *
 * @param {OrderAmounts} pedido Pedido (o pedido sintético de mesa) con sus montos crudos.
 * @returns {number} El total del servidor, redondeado a centavos.
 */
export function printedOrderTotal(pedido: OrderAmounts): number {
  return roundToCents(toFiniteAmount(pedido.total));
}

/**
 * Cuánto le falta (o le sobra) al desglose impreso para sumar el total que se imprime.
 *
 * `includeShipping` refleja si ESA ruta imprime la línea de envío: el residuo debe espejear
 * las líneas del papel, no una fórmula abstracta, o el ticket seguiría sin cuadrar. La propina
 * SIEMPRE cuenta como componente aunque el perfil oculte su línea (`showTip=false`): ocultarla
 * y luego reaparecerla como "Ajuste" sería des-ocultarla con otro nombre.
 *
 * @param {OrderAmounts} pedido Pedido (o pedido sintético de mesa) con sus montos crudos.
 * @param {{ includeShipping: boolean }} options `includeShipping`: si esta ruta imprime el envío.
 * @returns {number} La diferencia redondeada a centavos, o 0 si es ruido (≤ 1 centavo).
 */
export function computeBreakdownResidual(pedido: OrderAmounts, options: { includeShipping: boolean }): number {
  const breakdown = roundToCents(
    toFiniteAmount(pedido.subtotal) -
      toFiniteAmount(pedido.coupon_amount) +
      toFiniteAmount(pedido.propina) +
      (options.includeShipping ? toFiniteAmount(pedido.costo_envio) : 0),
  );
  const residual = roundToCents(printedOrderTotal(pedido) - breakdown);
  return Math.abs(residual) > RESIDUAL_EPSILON ? residual : 0;
}
